/**
 * The static header parameters, read-only (sheet 11 callout 5; FR-006; GEN.3,
 * GEN.6; style guide §2.6, §7.2, §8; DM-01M3C8MKBH360E5HRVH5KXF9TP).
 *
 * Editing is sheet 40's. Each bureau shows the API's `masked` value in mono and
 * Entered when `set`, else the neutral Not entered badge with the GEN.6
 * sentence. The mask is text only: never in a `title`, an `aria-label` or any
 * other attribute, and `set` is never rendered as text.
 *
 * Mission 7 WP11 (T060; FR-016; TX.2; squad E14): the card also reads
 * `bureausQuery()`. A bureau whose effective credential status is `verified`
 * shows `BureauStatusBadge` Verified in place of Entered or Not entered; any
 * other status keeps the badge above. The bureaus read is an enrichment: while
 * it loads, or when it fails, the card renders exactly as before, with no
 * error, because the new-run screen must not gain an alert for it.
 */
import { useQuery } from '@tanstack/react-query'

import { BureauStatusBadge } from '@/components/trellis/BureauStatusBadge'
import { MaskedValue } from '@/components/trellis/MaskedValue'
import { Badge } from '@/components/ui/badge'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { BUREAUS, type Bureau } from '@/lib/api/headerParams'
import { bureausQuery, headerParamsQuery } from '@/lib/queries'
import { copyText } from '@/lib/provisional-copy'

const BUREAU_NAMES: Record<Bureau, string> = {
  equifax: 'Equifax',
  experian: 'Experian',
  transunion: 'TransUnion',
  innovis: 'Innovis',
}

export function StaticHeaderCard() {
  const params = useQuery(headerParamsQuery())
  const bureaus = useQuery(bureausQuery())
  const verified = new Set((bureaus.data?.bureaus ?? []).filter((b) => b.status === 'verified').map((b) => b.bureau))
  return (
    <Card data-static-header="">
      <CardHeader>
        <CardTitle>Static header parameters</CardTitle>
        <p className="text-body-sm text-navy-600">{copyText('staticVersusPerRun')}</p>
      </CardHeader>
      {params.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-5 w-64" />
        </div>
      ) : params.isError ? (
        <p className="text-body-sm text-[var(--color-error-700)]">{copyText('genericError')}</p>
      ) : (
        <dl className="flex flex-col gap-3">
          <div className="flex items-baseline gap-4">
            <dt className="w-40 text-body-sm text-navy-600">Reporter name</dt>
            <dd className="text-body text-navy-950">{params.data.reporter_name}</dd>
          </div>
          {BUREAUS.map((bureau) => {
            const id = params.data.program_identifiers[bureau]
            const entered = id.set
            return (
              <div key={bureau} data-bureau={bureau} className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <dt className="w-40 text-body-sm text-navy-600">{BUREAU_NAMES[bureau]}</dt>
                <dd className="flex items-center gap-3">
                  {entered && id.masked !== null ? <MaskedValue value={id.masked} /> : null}
                  {verified.has(bureau) ? (
                    <BureauStatusBadge status="verified" />
                  ) : entered ? (
                    <Badge status="success">{copyText('entered')}</Badge>
                  ) : (
                    <Badge status="neutral">{copyText('notEntered')}</Badge>
                  )}
                </dd>
                {entered ? null : <dd className="w-full pl-44 text-caption text-navy-600">{copyText('notEnteredGen6')}</dd>}
              </div>
            )
          })}
        </dl>
      )}
    </Card>
  )
}
